/**
 * What a failed encryption operation says to a MEMBER.
 *
 * The store's `state.error` carries `{ code, message, ... }`, where `message`
 * is the API's own — `Device is APPROVED, not PENDING`, `The signature does not
 * prove possession of the key`, `No live challenge: request one and sign
 * again`, or axios' `Network Error`. Those are developer diagnostics. Printing
 * one on the screen where a member decides whether to trust a device tells them
 * nothing they can act on and reads as a broken app.
 *
 * `code` is on the error object precisely so a screen need not show the wire
 * text (`courses-encryption-api.js`, `ENCRYPTION_ERROR_CODES`). This maps each
 * code to one sentence, and anything it has never heard of — a thrown
 * TypeError, the store's own argument guards — to a generic one. `message`
 * stays for logging, never for rendering.
 *
 * A screen with a sharper sentence for one code passes it as `overrides`: an
 * approve screen knows a 409 means "already answered", where the same code on
 * a registration means "five devices are already waiting".
 */
import { ENCRYPTION_ERROR_CODES } from '@/utils/courses-encryption-api';

/** Said when the code is absent or unrecognised, so nothing is ever silent. */
export const FALLBACK_ERROR_COPY = 'Something went wrong. Try again in a moment.';

export const ERROR_COPY = Object.freeze({
  // Retryable: the two-minute challenge aged out or an earlier attempt spent
  // it, and the store already retried once with a fresh one.
  [ENCRYPTION_ERROR_CODES.STALE_NONCE]: 'That took too long to confirm. Tap Try again.',
  // Not retryable: the key is wrong, not the timing.
  [ENCRYPTION_ERROR_CODES.SIGNATURE_REJECTED]:
    'This device could not prove it holds your notes key, so nothing was changed.',
  // Not a key failure: a matured "Start over" hold completed server-side and
  // the account is unenrolled. Never say the key is wrong to someone whose own
  // reset just succeeded. True on the 404 lane too, where a member may simply
  // never have enrolled — which is why it does not say "has been reset".
  [ENCRYPTION_ERROR_CODES.NOT_ENROLLED]:
    'Your notes key is no longer set up on this account. Set up Next Steps again to start fresh.',
  [ENCRYPTION_ERROR_CODES.NOT_FOUND]: 'That is no longer there. It may already have been done.',
  [ENCRYPTION_ERROR_CODES.CONFLICT]:
    'Something has already changed. Open Next Steps again to see where things stand.',
  [ENCRYPTION_ERROR_CODES.NOT_CONFIGURED]:
    'Encrypted notes are not switched on yet. Try again later.',
  [ENCRYPTION_ERROR_CODES.BAD_REQUEST]: 'This device could not make that request. Update the app.',
  [ENCRYPTION_ERROR_CODES.UNAUTHORIZED]: 'You have been signed out. Sign in again to carry on.',
  [ENCRYPTION_ERROR_CODES.NETWORK]: 'No connection. Check your internet and tap Try again.',
  [ENCRYPTION_ERROR_CODES.UNKNOWN]: FALLBACK_ERROR_COPY,
});

/**
 * @param {{ code?: string }|string|null|undefined} error the store's
 *   `state.error`, or a bare code
 * @param {Record<string, string>} [overrides] one screen's sharper sentences,
 *   keyed by the same codes
 * @returns {string} a sentence written for a member — never `error.message`
 */
export function errorCopy(error, overrides = {}) {
  if (typeof error === 'string')
    return overrides[error] || ERROR_COPY[error] || FALLBACK_ERROR_COPY;
  // `apiCode` FIRST, because it is the sharper of the two: `code` collapses
  // every 409 into CONFLICT, so a screen that wants to separate two of them has
  // only this to key on. Falls straight through when a screen overrides nothing
  // for it, which is every screen that does not care.
  return (
    overrides[error?.apiCode] ||
    overrides[error?.code] ||
    ERROR_COPY[error?.code] ||
    FALLBACK_ERROR_COPY
  );
}
